// Подложка карты: КТО её отдаёт и на каких условиях.
//
// Раньше адрес тайла был константой в четырёх местах. 27.08.2026 Carto ввёл
// обязательный ключ и начал штамповать «API KEY REQUIRED» поверх карты — и
// стало видно, что «чья подложка» это настройка ДОМЕНА, а не свойство кода:
// латвийскому партнёру российская картография неуместна ровно так же, как ему
// уже неуместна наша палитра.
//
// ОШИБАЕМСЯ ВСЕГДА В СТОРОНУ CARTO. Подложка, которой мы не поняли, — это серый
// экран с висящими в пустоте полями: Яндекс на плохой ключ отвечает 403, а не
// ухудшенным тайлом (проба 27.08.2026), то есть не рисует НИЧЕГО. Нарисованная
// не той гаммой карта — беда меньшая, чем ненарисованная.

/** Как подписывать подложку. Условия ОБОИХ поставщиков этого требуют, и
 *  сегодня не выполнено ни одно: атрибуции в витрине нет ни одной.
 *
 *  Артворк знака здесь НЕ лежит: `logo` говорит «подписывается знаком», а какой
 *  это знак, решает компонент по `provider`. Адрес ассета зависит от сборки
 *  (у витрины `publicDir` — каталог фавиконок, своего `public/` нет), и,
 *  просочившись сюда, он привязал бы чистую логику к устройству Vite. */
export type Attribution =
  /** Готовая строка. НЕ переводится: «© OpenStreetMap, © CARTO» — юридическая
   *  формулировка из условий Carto, а не подпись интерфейса. */
  | { kind: 'text'; text: string; href: string }
  /** Знак поставщика. Несёт КЛЮЧ подписи, а не слово: чистая логика слов не
   *  знает — их подставляет компонент (правило двуязычного кабинета). */
  | { kind: 'logo'; altKey: string; href: string }

export interface BasemapSpec {
  provider: 'carto' | 'yandex'
  tiles: string[]
  tileSize: number
  /** Потолок зума ИСТОЧНИКА. `undefined` — не задавать вовсе (у MapLibre своё
   *  умолчание). У Carto его нет с самого начала, и проставить его «для
   *  аккуратности» значило бы поменять поведение домена, который ничего не
   *  просил. У Яндекса он назван документацией: z от 0 до 20. */
  maxzoom?: number
  attribution: Attribution
}

/** Настройка домена из бренд-пака. Ключ подмешивает сервер: пак версионируется,
 *  а ключ ротируется, и в репозитории ему не место. */
export interface BasemapConfig {
  provider?: string
  apikey?: string
}

/** Язык кабинета → шкала Яндекса.
 *
 *  Незнакомый язык уходит в английский, а НЕ в пустое значение: `lang` у
 *  Яндекса обязателен, и пустым он даёт 400 на каждый тайл — то есть карту без
 *  карты. Латышского в списке Яндекса нет вовсе (`ru_RU en_RU en_US uk_UA
 *  ru_UA tr_TR`), и это одна из причин, по которым латвийский домен остаётся
 *  на Carto. */
const YANDEX_LANG: Record<string, string> = { ru: 'ru_RU', en: 'en_US' }

/** Шаблон тайла Яндекса для MapLibre.
 *
 *  `projection=web_mercator` ОБЯЗАТЕЛЕН. По умолчанию Яндекс отдаёт
 *  эллиптический меркатор (WGS 84), а вся наша геометрия — сферический
 *  Web Mercator; без параметра подложка легла бы под данными со сдвигом по
 *  широте, растущим к полюсу. Отказ был бы немым: карта рисуется, берега
 *  выглядят правдоподобно, а поля стоят не над своей водой. */
export function yandexTiles(apikey: string, lang: string): string {
  const l = YANDEX_LANG[lang] ?? 'en_US'
  return (
    'https://tiles.api-maps.yandex.ru/v1/tiles/' +
    `?apikey=${encodeURIComponent(apikey)}&lang=${l}` +
    '&x={x}&y={y}&z={z}&l=map&projection=web_mercator'
  )
}

/** Нынешняя подложка, слово в слово. Домен без настройки обязан выглядеть в
 *  точности как до этой правки — то же правило, что у бренд-палитр. */
export const CARTO_BASEMAP: BasemapSpec = {
  provider: 'carto',
  tiles: ['https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png'],
  tileSize: 256,
  attribution: {
    kind: 'text',
    text: '© OpenStreetMap, © CARTO',
    href: 'https://carto.com/attributions',
  },
}

/** Подложка домена. `null`, незнакомый поставщик и поставщик без ключа — Carto. */
export function basemapFrom(cfg: BasemapConfig | null, lang: string): BasemapSpec {
  if (cfg?.provider === 'yandex' && cfg.apikey) {
    return {
      provider: 'yandex',
      tiles: [yandexTiles(cfg.apikey, lang)],
      tileSize: 256,
      maxzoom: 20,
      attribution: {
        kind: 'logo',
        altKey: 'map.basemap.yandex',
        href: 'https://yandex.ru/maps',
      },
    }
  }
  if (cfg?.provider === 'carto' && cfg.apikey) {
    return {
      ...CARTO_BASEMAP,
      tiles: [`${CARTO_BASEMAP.tiles[0]}?key=${encodeURIComponent(cfg.apikey)}`],
    }
  }
  return CARTO_BASEMAP
}
